<template>
    <div class="license-field">
        <div class="license-field__field">
            <PocketInput
                v-model="licenseKey"
                label="License Code"
                placeholder="15 digit code"
                class="license-field__license"
                :error="!!errors.length"
                :is-dark-mode="isDarkMode"
                @focusout="validateLicense({ checkShort: true })"
            />
            <Icon
                v-if="isLoading || isLoadingValidation"
                v-dark
                type="loading2"
                class="license-field__loading"
                :is-dark-mode="isDarkMode"
            />
        </div>
        <Errors
            v-if="errors.length"
            class="license-field__errors"
            :errors="errors"
            :is-dark-mode="isDarkMode"
        />
        <div
            v-if="licenseExams && licenseExams.length && licenseExams.length === 1"
            v-dark
            class="license-field__label"
        >
            Exam
        </div>
        <div
            v-else-if="licenseExams && licenseExams.length && licenseExams.length > 1"
            v-dark
            class="license-field__label"
        >
            {{ licenseExams.length }} Exams
        </div>
        <div
            v-for="(licenseExam, index) in licenseExams"
            :key="index"
            class="license-field__list"
        >
            <!-- @todo update uikit to support nest-server types -->
            <ExamCard
                :exam="licenseExam as Study.Class.ExamMetadataJSON"
                :bundle="getLicenseExamCardBundleProp(licenseExam)"
                class="license-field__exam"
                :disable-interaction="true"
                :is-dark-mode="isDarkMode"
            />
        </div>
        <div v-if="licenseExams && licenseExams.length" class="license-field__not-correct">
            <PocketLink
                class="license-field__not-correct-link"
                tabindex="0"
                type="tertiary-small"
                :is-dark-mode="isDarkMode"
                @click="showIntercom"
            >
                Contact our support team
            </PocketLink>
            if this list of exams doesn't seem correct.
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import { bundleModule } from '@/store/bundle/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { licenseModule } from '@/store/license/module'
import { userModule } from '@/store/user/module'
import { isSupportWindow } from '@/utils'
import type { Study } from '@pocketprep/types'
import type { ClientExam } from '@/store/bundle/state'

const {
    Link: PocketLink,
    Errors,
    Input: PocketInput,
    Icon,
    ExamCard,
} = UIKit

const props = withDefaults(
    defineProps<{
        modelValue?: string
    }>(),
    {
        modelValue: '',
    }
)

const emit = defineEmits<{
    'update:modelValue': [value: string | null]
}>()

const route = useRoute()
const router = useRouter()

const isLoading = ref(true)
const isLoadingValidation = ref(false)
const validatedLicense = ref<null | Study.Class.LicenseJSON>(null)
const licenseKey = ref<null | string>(null)
const validateDelayTimer = ref<null | ReturnType<typeof setTimeout>>(null)
const errors = ref<string[]>([])

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const licenseExams = computed(() => {
    const les: (Study.Class.ExamMetadataJSON | ClientExam)[] = []

    if (validatedLicense.value?.examGuid) {
        // Convert a license to a licenseExam
        const licenseExam = examMetadataModule.getters.getMostRecentExamForExamGuid(validatedLicense.value.examGuid)
        les.push(licenseExam)
        if (validatedLicense.value.linkedLicenses) {
            validatedLicense.value.linkedLicenses.forEach(linkedLicense => {
                // convert a license to a licenseExam
                const ll = linkedLicense as Study.Class.LicenseJSON
                if (ll?.examGuid) {
                    const linkedLicenseExam = examMetadataModule.getters.getMostRecentExamForExamGuid(ll.examGuid)
                    les.push(linkedLicenseExam)
                }
            })
        }
    }
    return les
})

// UI-Kit ExamCard only wants the bundle prop to have objectId: string and name: string
const getLicenseExamCardBundleProp = (licenseExam: Study.Class.ExamMetadataJSON | ClientExam) => {
    const le = licenseExam
    const leBundle = le && bundleModule.getters.getBundles()
        .find(b => b.exams.find(e => e.objectId === le.objectId))
    return {
        objectId: leBundle?.objectId as string,
        name: leBundle?.name as string,
    }
}

onMounted(async () => {
    licenseKey.value = props.modelValue || (typeof route.query.license === 'string'
        ? route.query.license
        : '')

    if (
        !licenseKey.value
        && typeof route.query.groupCode === 'string'
        && (typeof route.query.studentLmsId === 'string'
            || route.query.studentLmsId === undefined)
    ) {
        const groupCode = route.query.groupCode
        const studentLmsId = route.query.studentLmsId
        try {
            const licenseCodeForGroupCode = await licenseModule.actions.fetchLicenseCodeForGroupCode(
                groupCode,
                studentLmsId
            )
            if (licenseCodeForGroupCode) {
                licenseKey.value = licenseCodeForGroupCode
            }
        } catch (err) {
            errors.value = [ String(err) ]
        }
    }

    await Promise.all([
        examMetadataModule.actions.fetchExamMetadata(),
    ])

    isLoading.value = false
})

const validateLicense = (options: { checkShort: boolean }) => {
    // Skip validation if we already validated the current license key
    if (licenseKey.value === validatedLicense.value?.licenseKey) {
        return
    }

    validatedLicense.value = null
    errors.value = []

    if (validateDelayTimer.value) {
        clearTimeout(validateDelayTimer.value)
    }
    if (licenseKey.value?.match(/[A-Z0-9]{5}-[A-Z0-9]{5}-[A-Z0-9]{5}/)) {
        isLoadingValidation.value = true
        // setTimeout doesn't expect a promise return type, but still works as expected
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        validateDelayTimer.value = setTimeout(async () => {
            try {
                if (licenseKey.value) {
                    validatedLicense.value = await licenseModule.actions.validateLicense(licenseKey.value)
                }
                // If license is valid, we should remove the license qs params from url (if present)
                if (route.query.license) {
                    await router.replace({
                        query: {
                            ...route.query,
                            license: undefined,
                            firstName: undefined,
                            lastName: undefined,
                        },
                    })
                }
            } catch (err) {
                const error = (err as { error?: string } | undefined)?.error
                if (!err) {
                    errors.value = [ 'That license code isn\'t valid. Try again?' ]
                } else if (error) {
                    errors.value = [ error ]
                } else if (err instanceof Error || typeof err === 'string') {
                    errors.value = [ err.toString() ]
                }
            } finally {
                isLoadingValidation.value = false
            }
        }, 200)
    } else if (
        (options.checkShort && licenseKey.value?.length)
        || (!options.checkShort && licenseKey.value?.length === 17)
    ) {
        errors.value = [ 'That license code isn’t valid. License codes are case sensitive and should be in the '
            + 'format: XXXXX-XXXXX-XXXXX' ]
    }
}

watch(licenseKey, () => {
    validateLicense({ checkShort: false })
})

const updatedValidLicense = (license: Study.Class.LicenseJSON) => {
    if (license) {
        emitUpdateModelValue(license.licenseKey)
    } else {
        emitUpdateModelValue(null)
    }

}

watch(validatedLicense, (license) => {
    updatedValidLicense(license as Study.Class.LicenseJSON)
})

const showIntercom = () => {
    /* istanbul ignore next */
    if (import.meta.env.VUE_APP_INTERCOM_APP_ID && !isSupportWindow()) {
        window.Intercom('show')
    }
}

const emitUpdateModelValue = (licenseKeyParam: string | null) => {
    emit('update:modelValue', licenseKeyParam)
}
</script>

<style lang="scss" scoped>
.license-field {
    &__field {
        position: relative;
    }

    &__license,
    &__exam,
    &__errors {
        margin-left: -12px;
        margin-right: -12px;
        width: calc(100% + 24px);
        box-sizing: border-box;
    }

    &__loading {
        position: absolute;
        bottom: 8px;
        width: 20px;
        height: 20px;
        right: -1px;
    }

    &__license {
        margin-bottom: 23px;
    }

    &__errors {
        margin-top: -12px;
        margin-bottom: 40px;
    }

    &__label {
        font-size: 13px;
        line-height: 14px;
        color: $slate-03;
        margin-bottom: 9px;

        &--dark {
            color: $fog;
        }
    }

    &__exam {
        margin-bottom: 14px;
    }

    &__not-correct {
        font-size: 14px;
        line-height: 17px;
        margin-bottom: 28px;
    }
}
</style>
